import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Card, Badge, Alert } from '@components/bootstrap';
import './ChangelogViewer.scss';
const ChangelogViewer = () => {
    const [changelog, setChangelog] = useState('');
    const [entries, setEntries] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    useEffect(() => {
        fetchChangelog();
    }, []);
    const fetchChangelog = async () => {
        try {
            setLoading(true);
            const response = await fetch('/docs/CHANGELOG.md');
            if (!response.ok) {
                throw new Error('Não foi possível carregar o CHANGELOG');
            }
            const content = await response.text();
            const decodedContent = decodeURIComponent(escape(content));
            setChangelog(decodedContent);
            parseChangelog(decodedContent);
        }
        catch (err) {
            setError('Erro ao carregar o histórico de desenvolvimento');
            console.error('Error fetching changelog:', err);
        }
        finally {
            setLoading(false);
        }
    };
    const parseChangelog = (content) => {
        const lines = content.split('\n');
        const parsedEntries = [];
        const categories = {};
        let currentCategory = '';
        let currentEntry = {};
        let inEntry = false;
        let entryContent = [];
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            // Detectar categoria (## Nome da Categoria)
            if (line.startsWith('## ') && !line.startsWith('### ')) {
                // Salvar entrada anterior se existir
                if (inEntry && currentEntry.title && currentCategory) {
                    const entry = {
                        title: currentEntry.title,
                        file: currentEntry.file || '',
                        lastModified: currentEntry.lastModified || '',
                        content: entryContent.join('\n').trim(),
                        category: currentCategory
                    };
                    parsedEntries.push(entry);
                    categories[currentCategory] = (categories[currentCategory] || 0) + 1;
                }
                // Nova categoria
                const categoryName = line.substring(3).trim();
                // Mapear categorias para nomes mais amigáveis
                currentCategory = mapCategoryName(categoryName);
                inEntry = false;
                entryContent = [];
                continue;
            }
            // Detectar entrada (### Nome da Entrada)
            if (line.startsWith('### ') && currentCategory) {
                // Salvar entrada anterior se existir
                if (inEntry && currentEntry.title) {
                    const entry = {
                        title: currentEntry.title,
                        file: currentEntry.file || '',
                        lastModified: currentEntry.lastModified || '',
                        content: entryContent.join('\n').trim(),
                        category: currentCategory
                    };
                    parsedEntries.push(entry);
                    categories[currentCategory] = (categories[currentCategory] || 0) + 1;
                }
                // Nova entrada
                currentEntry = {
                    title: line.substring(4).trim()
                };
                inEntry = true;
                entryContent = [];
                continue;
            }
            // Extrair metadados da entrada
            if (inEntry) {
                const fileMatch = line.match(/\*\*Arquivo:\*\* (.+)/);
                const dateMatch = line.match(/\*\*Ultima modificacao:\*\* (.+)/);
                if (fileMatch) {
                    currentEntry.file = fileMatch[1];
                }
                else if (dateMatch) {
                    currentEntry.lastModified = dateMatch[1];
                }
                else if (line && !line.startsWith('---')) {
                    // Adicionar ao conteúdo da entrada (ignorar separadores)
                    entryContent.push(line);
                }
            }
        }
        // Salvar última entrada se existir
        if (inEntry && currentEntry.title && currentCategory) {
            const entry = {
                title: currentEntry.title,
                file: currentEntry.file || '',
                lastModified: currentEntry.lastModified || '',
                content: entryContent.join('\n').trim(),
                category: currentCategory
            };
            parsedEntries.push(entry);
            categories[currentCategory] = (categories[currentCategory] || 0) + 1;
        }
        setEntries(parsedEntries);
        setStats({
            totalLogs: parsedEntries.length,
            lastUpdate: new Date().toLocaleDateString('pt-BR'),
            categories
        });
    };
    const mapCategoryName = (categoryName) => {
        const categoryMap = {
            'Debug': '🔧 Debug',
            'Implementacoes': '🚀 Implementações',
            'Correcoes': '🐛 Correções',
            'Integracoes': '🔗 Integrações',
            'Padronizacao': '📋 Padronização',
            'Testes': '🧪 Testes',
            'Melhorias': '✨ Melhorias',
            'Outros': '📝 Outros'
        };
        return categoryMap[categoryName] || categoryName;
    };
    const getCategoryBadgeVariant = (category) => {
        switch (category) {
            case '🔧 Debug': return 'info';
            case '🚀 Implementações': return 'primary';
            case '🐛 Correções': return 'danger';
            case '🔗 Integrações': return 'success';
            case '📋 Padronização': return 'warning';
            case '🧪 Testes': return 'secondary';
            case '✨ Melhorias': return 'light';
            case '📝 Outros': return 'dark';
            default: return 'secondary';
        }
    };
    const filteredEntries = entries.filter(entry => {
        const matchesSearch = entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            entry.content.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || entry.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });
    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('pt-BR');
        }
        catch {
            return dateString;
        }
    };
    if (loading) {
        return (_jsx("div", { className: "changelog-viewer", children: _jsxs("div", { className: "text-center py-5", children: [_jsx("div", { className: "spinner-border text-primary", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Carregando..." }) }), _jsx("p", { className: "mt-3", children: "Carregando hist\u00F3rico de desenvolvimento..." })] }) }));
    }
    if (error) {
        return (_jsx("div", { className: "changelog-viewer", children: _jsxs(Alert, { variant: "danger", children: [_jsx("h5", { children: "Erro ao carregar hist\u00F3rico" }), _jsx("p", { children: error }), _jsx("button", { className: "btn btn-outline-danger btn-sm", onClick: fetchChangelog, children: "Tentar novamente" })] }) }));
    }
    return (_jsxs("div", { className: "changelog-viewer", children: [_jsxs(Card, { className: "mb-4", children: [_jsx("div", { className: "card-header", children: _jsx("h5", { className: "mb-0", children: "\uD83D\uDCCB Hist\u00F3rico de Desenvolvimento" }) }), _jsx("div", { className: "card-body", children: stats && (_jsxs("div", { className: "row", children: [_jsx("div", { className: "col-md-3", children: _jsxs("div", { className: "stat-item", children: [_jsx("h6", { children: "Total de Logs" }), _jsx("span", { className: "stat-value", children: stats.totalLogs })] }) }), _jsx("div", { className: "col-md-3", children: _jsxs("div", { className: "stat-item", children: [_jsx("h6", { children: "\u00DAltima Atualiza\u00E7\u00E3o" }), _jsx("span", { className: "stat-value", children: stats.lastUpdate })] }) }), _jsx("div", { className: "col-md-6", children: _jsxs("div", { className: "stat-item", children: [_jsx("h6", { children: "Categorias" }), _jsx("div", { className: "category-badges", children: Object.entries(stats.categories).map(([category, count]) => (_jsxs(Badge, { variant: getCategoryBadgeVariant(category), className: "me-2 mb-1", children: [category, ": ", count] }, category))) })] }) })] })) })] }), _jsx(Card, { className: "mb-4", children: _jsx("div", { className: "card-body", children: _jsxs("div", { className: "row", children: [_jsx("div", { className: "col-md-6", children: _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "\uD83D\uDD0D Buscar" }), _jsx("input", { type: "text", className: "form-control", placeholder: "Buscar por t\u00EDtulo ou conte\u00FAdo...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }) }), _jsx("div", { className: "col-md-6", children: _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "\uD83D\uDCC2 Categoria" }), _jsxs("select", { className: "form-select", value: selectedCategory, onChange: (e) => setSelectedCategory(e.target.value), children: [_jsx("option", { value: "all", children: "Todas as categorias" }), stats && Object.keys(stats.categories).map(category => (_jsx("option", { value: category, children: category }, category)))] })] }) })] }) }) }), _jsx("div", { className: "changelog-entries", children: filteredEntries.length === 0 ? (_jsx(Alert, { variant: "info", className: "text-center", children: "Nenhuma entrada encontrada com os filtros aplicados." })) : (filteredEntries.map((entry, index) => (_jsxs(Card, { className: "mb-3 changelog-entry", children: [_jsx("div", { className: "card-header", children: _jsxs("div", { className: "d-flex justify-content-between align-items-start", children: [_jsxs("div", { className: "flex-grow-1", children: [_jsx("h6", { className: "mb-1", children: entry.title }), _jsx("div", { className: "entry-meta", children: _jsxs("small", { className: "text-muted", children: ["\uD83D\uDCC4 ", entry.file, " | \uD83D\uDCC5 ", formatDate(entry.lastModified)] }) })] }), _jsx(Badge, { variant: getCategoryBadgeVariant(entry.category), children: entry.category })] }) }), _jsx("div", { className: "card-body", children: _jsx("div", { className: "entry-content", dangerouslySetInnerHTML: {
                                    __html: entry.content.replace(/\n/g, '<br>')
                                } }) })] }, index)))) }), filteredEntries.length > 0 && (_jsx("div", { className: "mt-4 text-center", children: _jsxs("small", { className: "text-muted", children: ["Mostrando ", filteredEntries.length, " de ", entries.length, " entradas"] }) }))] }));
};
export default ChangelogViewer;
